import React, { useState, useEffect } from 'react';
import '../styles/profile.css';
import { useLanguage } from '../utils/LanguageContext';

const EditProfileModal = ({ isOpen, onClose, userData, onSave }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    first_name: '',
    last_name: '',
    bio: '',
    phoneNumber: '',
    region: '',
  });

  const [loading, setLoading] = useState(false);
  const [profilePic, setProfilePic] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (userData) {
      setFormData({
        name: userData.name || '',
        first_name: userData.user?.first_name || '',
        last_name: userData.user?.last_name || '',
        bio: userData.bio || '',
        phoneNumber: userData.phoneNumber || '',
        region: userData.region || '',
      });
      setProfilePic(null);
      setPreviewUrl(null);
    }
  }, [userData]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePic(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handlePhoneChange = (e) => {
    let val = e.target.value;
    let digits = val.replace(/\D/g, '');

    if (digits.length === 0) {
      setFormData(prev => ({ ...prev, phoneNumber: '' }));
      return;
    }

    if (!digits.startsWith('998')) {
      digits = '998' + digits;
    }

    let formatted = '+998';
    if (digits.length > 3) {
      formatted += ' (' + digits.substring(3, 5);
    }
    if (digits.length > 5) {
      formatted += ') ' + digits.substring(5, 8);
    }
    if (digits.length > 8) {
      formatted += '-' + digits.substring(8, 10);
    }
    if (digits.length > 10) {
      formatted += '-' + digits.substring(10, 12);
    }

    setFormData(prev => ({ ...prev, phoneNumber: formatted }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submissionData = new FormData();
      submissionData.append('name', formData.name);
      submissionData.append('first_name', formData.first_name);
      submissionData.append('last_name', formData.last_name);
      submissionData.append('bio', formData.bio);
      submissionData.append('phoneNumber', formData.phoneNumber);
      submissionData.append('region', formData.region);

      if (profilePic) {
        submissionData.append('profile_picture', profilePic);
      }

      await onSave(submissionData);
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const currentAvatar = userData?.profile_picture
    ? `http://127.0.0.1:8000${userData.profile_picture}`
    : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content animate-slide-up" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2><i className="fa-solid fa-user-pen" style={{ marginRight: '10px', color: '#22c55e' }}></i>{t('prof_modal_title') || 'Edit Profile'}</h2>
          <button className="btn-close" onClick={onClose}>
            <i className="fa-solid fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="edit-profile-form">
          {/* Profile Picture Upload */}
          <div className="form-group">
            <label>Profile Photo</label>
            <div className="avatar-upload-area">
              <div className="avatar-preview">
                {(previewUrl || currentAvatar) ? (
                  <img src={previewUrl || currentAvatar} alt="Preview" />
                ) : (
                  <i className="fa-solid fa-user avatar-placeholder-icon"></i>
                )}
              </div>
              <div className="avatar-upload-info">
                <label htmlFor="profile-pic-input" className="btn-upload">
                  <i className="fa-solid fa-cloud-arrow-up"></i> Choose Photo
                </label>
                <input
                  id="profile-pic-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <span className="upload-hint">JPG, PNG — max 5MB</span>
              </div>
            </div>
          </div>

          {/* Display Name */}
          <div className="form-group">
            <label>{t('prof_label_name') || 'Display Name'}</label>
            <div className="input-wrapper">
              <i className="fa-solid fa-at input-icon"></i>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your display name"
                required
              />
            </div>
          </div>

          {/* First Name & Last Name */}
          <div className="form-row">
            <div className="form-group half">
              <label>{t('auth_label_fname') || 'First Name'}</label>
              <div className="input-wrapper">
                <i className="fa-solid fa-user input-icon"></i>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder={t('auth_label_fname') || 'First name'}
                  required
                />
              </div>
            </div>

            <div className="form-group half">
              <label>{t('auth_label_lname') || 'Last Name'}</label>
              <div className="input-wrapper">
                <i className="fa-solid fa-user input-icon"></i>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder={t('auth_label_lname') || 'Last name'}
                  required
                />
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="form-group">
            <label>{t('prof_label_bio') || 'Bio'}</label>
            <div className="input-wrapper textarea-wrapper">
              <i className="fa-solid fa-pen-nib input-icon"></i>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                placeholder="Tell us about yourself..."
                rows="3"
              />
            </div>
          </div>

          {/* Phone & Region */}
          <div className="form-row">
            <div className="form-group half">
              <label>{t('prof_label_phone') || 'Phone Number'}</label>
              <div className="input-wrapper">
                <i className="fa-solid fa-phone input-icon"></i>
                <input
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="+998 (__) ___-__-__"
                  maxLength={19}
                  required
                />
              </div>
            </div>

            <div className="form-group half">
              <label>{t('prof_label_region') || 'Region'}</label>
              <div className="input-wrapper">
                <i className="fa-solid fa-location-dot input-icon"></i>
                <select
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t('auth_select_region') || 'Select Region'}</option>
                  <option value="Qoraqalpogʻiston Respublikasi">Qoraqalpogʻiston Respublikasi</option>
                  <option value="Andijon viloyati">Andijon viloyati</option>
                  <option value="Buxoro viloyati">Buxoro viloyati</option>
                  <option value="Fargʻona viloyati">Fargʻona viloyati</option>
                  <option value="Jizzax viloyati">Jizzax viloyati</option>
                  <option value="Xorazm viloyati">Xorazm viloyati</option>
                  <option value="Namangan viloyati">Namangan viloyati</option>
                  <option value="Navoiy viloyati">Navoiy viloyati</option>
                  <option value="Qashqadaryo viloyati">Qashqadaryo viloyati</option>
                  <option value="Samarqand viloyati">Samarqand viloyati</option>
                  <option value="Sirdaryo viloyati">Sirdaryo viloyati</option>
                  <option value="Surxondaryo viloyati">Surxondaryo viloyati</option>
                  <option value="Toshkent viloyati">Toshkent viloyati</option>
                  <option value="Toshkent shahri">Toshkent shahri</option>
                </select>
              </div>
            </div>
          </div>

          {/* Email — read only */}
          <div className="form-group">
            <label>{t('prof_label_email') || 'Email Address'}</label>
            <div className="input-wrapper">
              <i className="fa-solid fa-envelope input-icon"></i>
              <input
                type="email"
                value={userData?.user?.email || ''}
                disabled
                className="input-disabled"
              />
              <span className="locked-badge"><i className="fa-solid fa-lock"></i></span>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              {t('prof_btn_cancel') || 'Cancel'}
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? (
                <><i className="fa-solid fa-circle-notch fa-spin"></i> {t('prof_btn_saving') || 'Saving...'}</>
              ) : (
                <><i className="fa-solid fa-check"></i> {t('prof_btn_save') || 'Save Changes'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;
